import { createContext, ReactNode, useContext, useMemo } from "react";

import { useAppContext } from "../../hooks/useAppContext";
import { isUnderDevelopment } from "../../utils/featureFlag";
import {
  useDeviceRdmStateInternal,
  type DeviceRdmStateValue,
} from "./useDeviceRdmState";

type ScreenDeviceRdmContextValue = DeviceRdmStateValue & {
  screenId: string;
};

const ScreenDeviceRdmContext =
  createContext<ScreenDeviceRdmContextValue | null>(null);

/**
 * One getScreenDeviceState query + WS handler per screen (Screen Detail, modals).
 * Must be nested inside RealtimeRdmProvider.
 */
export function ScreenDeviceRdmProvider({
  screenId,
  children,
}: {
  screenId: string;
  children: ReactNode;
}) {
  const rdmEnabled = isUnderDevelopment(useAppContext());
  const { deviceState, lastCommand, isRdmOnline, buildDeviceInfo } =
    useDeviceRdmStateInternal(screenId, rdmEnabled);
  const value = useMemo(
    (): ScreenDeviceRdmContextValue => ({
      screenId,
      deviceState,
      lastCommand,
      isRdmOnline,
      buildDeviceInfo,
    }),
    [screenId, deviceState, lastCommand, isRdmOnline, buildDeviceInfo],
  );

  return (
    <ScreenDeviceRdmContext.Provider value={value}>
      {children}
    </ScreenDeviceRdmContext.Provider>
  );
}

export function useScreenDeviceRdmContext(): ScreenDeviceRdmContextValue | null {
  return useContext(ScreenDeviceRdmContext);
}
